import { useMemo, useState } from 'react';
import { Incident } from '../data/demoData';
import { apiFetch } from '../lib/apiClient';
import { FilterableColumnTable, type FilterableColumnDef } from './FilterableColumnTable';

interface Props {
  incident: Incident;
  customerId: string;
  onOpenIncident?: (incidentNumber: string) => void;
}

interface SimilarIncidentRow {
  incidentNumber: string;
  title: string;
  severity: string;
  status: string;
  createdTime: string;
  owner: string;
  classification: string;
  closureReason: string;
  score: number;
  scoreLabel: 'Low' | 'Medium' | 'High';
  matchReasons: string[];
  sharedEntities: string[];
  sharedTactics: string[];
  sharedAlerts: string[];
  resolutionSummary: string;
}

interface SimilarResult {
  ok: true;
  incidentId: string;
  customerId: string;
  generatedAt: string;
  lookback: string;
  matches: SimilarIncidentRow[];
  diagnostics: string[];
}

function getIncidentId(incident: Incident): string {
  return String((incident as unknown as Record<string, unknown>).armIncidentName || incident.id || incident.incidentNumber || '').trim();
}

function csvEscape(value: unknown): string {
  const text = Array.isArray(value) ? value.join('; ') : String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function exportCsv(filename: string, rows: SimilarIncidentRow[]) {
  const columns: Array<keyof SimilarIncidentRow> = ['incidentNumber', 'title', 'severity', 'status', 'createdTime', 'owner', 'classification', 'closureReason', 'score', 'scoreLabel', 'matchReasons', 'sharedEntities', 'sharedTactics', 'resolutionSummary'];
  const csv = [columns.join(','), ...rows.map((row) => columns.map((col) => csvEscape(row[col])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function scoreClass(label: string): string {
  if (label === 'High') return 'bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300';
  if (label === 'Medium') return 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300';
  return 'bg-muted text-muted-foreground';
}

export function IncidentSimilarityPanel({ incident, customerId, onOpenIncident }: Props) {
  const incidentId = useMemo(() => getIncidentId(incident), [incident]);
  const [result, setResult] = useState<SimilarResult | null>(null);
  const [lookback, setLookback] = useState('90d');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setLoading(true); setError(null);
    try {
      const data = await apiFetch<SimilarResult>(`/incidents/${encodeURIComponent(incidentId)}/similar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, incident, lookback }),
      });
      setResult(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  const columns: FilterableColumnDef<SimilarIncidentRow>[] = useMemo(() => [
    { key: 'score', header: 'Score', headerText: 'Score', value: (r) => r.score, render: (r) => <span className={`rounded-full px-2 py-1 text-[11px] font-bold ${scoreClass(r.scoreLabel)}`}>{r.score} · {r.scoreLabel}</span> },
    { key: 'incidentNumber', header: 'Incident', headerText: 'Incident', value: (r) => r.incidentNumber, render: (r) => <button onClick={(e) => { e.stopPropagation(); onOpenIncident?.(r.incidentNumber); }} className="font-semibold text-blue-600 hover:underline dark:text-blue-300">{r.incidentNumber || 'Open'}</button> },
    { key: 'title', header: 'Title', headerText: 'Title', value: (r) => r.title, render: (r) => <span className="break-words">{r.title}</span> },
    { key: 'severity', header: 'Severity', headerText: 'Severity', value: (r) => r.severity },
    { key: 'status', header: 'Status', headerText: 'Status', value: (r) => r.status },
    { key: 'classification', header: 'Classification', headerText: 'Classification', value: (r) => r.classification },
    { key: 'matchReasons', header: 'Match reasons', headerText: 'Match reasons', value: (r) => r.matchReasons.join('; '), render: (r) => <span>{r.matchReasons.join('; ')}</span> },
    { key: 'resolutionSummary', header: 'Prior resolution', headerText: 'Prior resolution', value: (r) => r.resolutionSummary, render: (r) => <span className="break-words">{r.resolutionSummary}</span> },
  ], [onOpenIncident]);

  return (
    <section className="mt-3 rounded-xl border border-fuchsia-200 dark:border-fuchsia-900 bg-fuchsia-50/60 dark:bg-fuchsia-950/20 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-fuchsia-900 dark:text-fuchsia-100">Incident Similarity Search</p>
          <p className="text-xs text-fuchsia-800/80 dark:text-fuchsia-200/80">Finds recurring incidents by title, entities, tactics, providers, alert context and prior closure/resolution patterns.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={lookback} onChange={(e) => setLookback(e.target.value)} className="rounded-lg border border-border bg-background px-2 py-2 text-xs">
            <option value="30d">30d</option><option value="60d">60d</option><option value="90d">90d</option><option value="180d">180d</option>
          </select>
          <button onClick={run} disabled={loading} className="rounded-lg bg-fuchsia-600 px-3 py-2 text-xs font-bold text-white hover:bg-fuchsia-700 disabled:opacity-60">{loading ? 'Searching…' : result ? 'Refresh Similarity' : 'Find Similar Incidents'}</button>
        </div>
      </div>
      {error && <div className="mt-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-2 text-xs text-red-700 dark:text-red-200">{error}</div>}
      {result && (
        <div className="mt-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">Matches</p><p className="text-lg font-bold">{result.matches.length}</p></div>
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">Top score</p><p className="text-lg font-bold">{result.matches[0]?.score ?? 0}</p></div>
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">Lookback</p><p className="text-lg font-bold">{result.lookback}</p></div>
          </div>
          <div className="rounded-lg border border-border bg-card p-3">
            <div className="mb-2 flex items-center justify-between gap-2"><p className="text-xs font-bold">Similar incidents</p><button onClick={() => exportCsv(`similar-incidents-${incident.incidentNumber || incidentId}.csv`, result.matches)} disabled={!result.matches.length} className="rounded-lg border border-border px-2.5 py-1.5 text-[11px] font-semibold hover:bg-muted disabled:opacity-50">Export CSV</button></div>
            <FilterableColumnTable rows={result.matches} columns={columns} storageKey={`incident-similarity-${incidentId}`} rowKey={(row, idx) => row.incidentNumber || idx} emptyMessage="No similar incidents found in the selected window." maxHeightClassName="max-h-[560px]" onRowClick={(row) => onOpenIncident?.(row.incidentNumber)} />
          </div>
          {result.diagnostics.length > 0 && <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-2 text-xs text-amber-800 dark:text-amber-200">{result.diagnostics.join(' ')}</div>}
        </div>
      )}
    </section>
  );
}
